import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchDashboardTiles,
  reorderDashboardTiles,
  selectActiveTileScope,
  selectDashboardTiles,
  selectDashboardTilesState
} from '../../store/entities/dashboardTiles';
import { FetchState } from '../../store/fetchState';
import { DashboardPage } from '@api/models/enums/DashboardPage';
import { DashboardTileDto } from '@api/models/dashboard/DashboardTileDto';
import { TileScope } from '@api/models/dashboard/TileScope';
import { ErrorAlert } from '../common/ErrorAlert';
import {
  DashboardGrid,
  FadeIn,
  OverviewEmptyHint,
  OverviewEmptyState,
  OverviewEmptyTitle,
  StepBody,
} from './AnalyticsStyles';
import { ReorderableGrid } from './components/ReorderableGrid';
import { SkeletonTiles } from './components/SkeletonTiles';
import { SortableTile } from './components/SortableTile';
import { useTileFetching } from './useTileFetching';

interface Props {
  onExploreTile: (tile: DashboardTileDto) => void;
}

const EMPTY_STATE_KEYS: Record<TileScope, { title: string; hint: string }> = {
  personal: {
    title: 'analytics.tile.emptyState',
    hint: 'analytics.tile.emptyStateHint',
  },
  company: {
    title: 'analytics.tile.emptyStateCompany',
    hint: 'analytics.tile.emptyStateCompanyHint',
  },
  global: {
    title: 'analytics.tile.emptyStateGlobal',
    hint: 'analytics.tile.emptyStateGlobalHint',
  },
};

export const AnalyticsOverview: FC<Props> = ({ onExploreTile }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const tiles = useAppSelector(selectDashboardTiles);
  const tilesState = useAppSelector(selectDashboardTilesState);
  const activeScope = useAppSelector(selectActiveTileScope);

  const loading = tilesState === FetchState.Loading || tilesState === FetchState.None;
  const loaded = tilesState === FetchState.Loaded;
  const hasError = tilesState === FetchState.Error;

  // Fetch tiles on mount and when scope changes
  useEffect(() => {
    dispatch(fetchDashboardTiles(DashboardPage.Overview, activeScope));
  }, [dispatch, activeScope]);

  const { tileReduxKey, handleRetryTile } = useTileFetching(tiles, loaded, loading);

  const getKey = useCallback((tile: DashboardTileDto) => tile.id, []);
  const handleReorder = useCallback((orderedIds: string[]) => dispatch(reorderDashboardTiles(orderedIds)), [dispatch]);

  const showEmptyState = loaded && tiles.length === 0;
  const emptyKeys = EMPTY_STATE_KEYS[activeScope];

  return (
    <FadeIn>
      <StepBody>
        {loading && (
          <DashboardGrid>
            <SkeletonTiles />
          </DashboardGrid>
        )}

        {hasError && <ErrorAlert />}

        {showEmptyState && (
          <OverviewEmptyState>
            <OverviewEmptyTitle>{t(emptyKeys.title)}</OverviewEmptyTitle>
            <OverviewEmptyHint>{t(emptyKeys.hint)}</OverviewEmptyHint>
          </OverviewEmptyState>
        )}

        {loaded && tiles.length > 0 && (
          <ReorderableGrid
            items={tiles}
            getKey={getKey}
            onReorder={handleReorder}
            renderItem={(tile) => (
              <SortableTile
                key={tile.id}
                tile={tile}
                reduxKey={tileReduxKey(tile.id)}
                onClick={() => onExploreTile(tile)}
                onRetry={() => handleRetryTile(tile)}
              />
            )}
          />
        )}
      </StepBody>
    </FadeIn>
  );
};
